import { AfterViewInit, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { PROPERTY_FIELD_MAX_LENGTH } from '@ee/common/forms';
import findKey from 'lodash-es/findKey';
import forEach from 'lodash-es/forEach';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, Subscription } from 'rxjs';
import {
  AddressSuggestion,
  County,
  CountySubdivisions,
  Court,
  IntakeFormSection,
  IntakeOption,
  Organization,
  SubdivisionKind,
  SubdivisionLookupResult,
  User
} from '@ee/common/models';
import {
  AccountType,
  IntakeFormFields,
  UNIT_TYPES,
  CaseAddressFillOptions,
  RolesEnum,
  SubdivisionSource
} from '@ee/common/enums';
import { STATES, STATES_COMPARATOR } from '@ee/common/constants';
import { CountyService } from '@ee/common/services';
import { MatDialog } from '@angular/material/dialog';
import { CourtChangeRequestDialogComponent, CourtChangeRequestDialogData } from '@ee/common/court-change-request';
import find from 'lodash-es/find';
import {
  courtRecommendation,
  matchRecommendedCourt,
  subdivisionLookupDecision,
  subdivisionLookupOutcome
} from '@ee/common/intake-layout';

@Component({
  selector: 'ee-eviction-property-section',
  template: `
    <div class="properties-form section-wrapper" [formGroup]="form">
      @if (options[intakeFields.PROPERTY_TYPE].show) {
        <div class="flex flex-row justify-start items-center">
          <div class="property-type-label mr-4">Property Type:</div>
          <mat-radio-group class="mr-4" aria-label="Property Type" formControlName="property_type">
            <mat-radio-button class="mr-2" [value]="'Conventional'">Conventional</mat-radio-button>
            <mat-radio-button class="mr-2" [value]="'Public Housing'">Public Housing</mat-radio-button>
            <mat-radio-button class="mr-2" [value]="'Section 8'">Section 8</mat-radio-button>
          </mat-radio-group>
          @if (form.get('property_type').touched && form.get('property_type').invalid) {
            <mat-error>Required</mat-error>
          }
        </div>
      }

      <div class="in-row-grid">
        @if (options[intakeFields.PROPERTY_NAME].show) {
          <mat-form-field class="quad-column">
            <mat-label>Property Name</mat-label>
            <input matInput formControlName="property_name" />
            @if (form.get('property_name').hasError('required')) {
              <mat-error>A property name is required.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.PROPERTY_NAME].required ? 'Required' : 'Optional' }}.
              {{ propertySectionLocalization?.['property_name_hint'] ?? 'An apartment or building name.' }}
            </mat-hint>
          </mat-form-field>
        }
      </div>

      @if (propertySection?.fields?.length && propertyFields) {
        <div class="in-row-grid" formGroupName="custom_fields">
          @for (f of propertySection?.fields; track f; let i = $index) {
            <ee-field
              [compact]="false"
              [ngClass]="{
                'margin-bottom-sm': i < propertySection.fields.length - 1,
                'md:mb-4': i < propertySection.fields.length - 1,
                'double-column': f.columns === 2,
                'triple-column': f.columns === 3,
                'quad-column': f.columns === 4
              }"
              [hideFromIntake]="f.hide_from_intake"
              [hidden]="f.hide_from_intake && !overrideFieldVisibility"
              [control]="$any(propertyFields.controls[f.field])"
              [field]="f"></ee-field>
          }
        </div>
      }

      <div class="in-row-grid">
        @if (
          selectedClient?.client_settings?.address_fill_option === caseAddressFillOptions.FILL_WITH_CLIENT_ADDRESS ||
          selectedClient?.client_settings?.address_fill_option === caseAddressFillOptions.LEAVE_BLANK
        ) {
          <mat-form-field class="w-full double-column">
            <mat-label>Street Address</mat-label>
            <input matInput formControlName="property_street_address" [autocomplete]="false" />
            @if (form.get('property_street_address').invalid && form.get('property_street_address').errors?.required) {
              <mat-error>Please enter the street address.</mat-error>
            }
            @if (form.get('property_street_address').hasError('maxlength')) {
              <mat-error>
                Street address can be at most
                {{ form.get('property_street_address').getError('maxlength').requiredLength }} characters.
              </mat-error>
            }
            <mat-hint>Required.</mat-hint>
          </mat-form-field>
        } @else {
          <ee-address-autocomplete
            class="double-column"
            placeholder="Street Address"
            [formControl]="$any(form.get('property_street_address'))"
            (selectionChange)="onAddressSelected($event)"
            [selectedState]="form.get('property_state').value"></ee-address-autocomplete>
        }
        <mat-form-field class="portal-remove-fixed-infix-width">
          <mat-label>Unit Type</mat-label>
          <mat-select formControlName="property_unit_type">
            <mat-option [value]="null">&nbsp;</mat-option>
            @for (type of unitTypeOptions; track type) {
              <mat-option [value]="type">
                {{ type }}
              </mat-option>
            }
          </mat-select>
          <mat-hint>Optional.</mat-hint>
        </mat-form-field>
        <mat-form-field>
          <mat-label>Unit #</mat-label>
          <!-- No native maxlength: it silently cuts a pasted or autofilled value, saving a wrong unit. -->
          <input matInput formControlName="property_unit_number" />
          <mat-hint>Optional.</mat-hint>
          @if (form?.get('property_unit_number')?.hasError('maxlength')) {
            <mat-error>Unit # can be at most {{ unitNumberMax }} characters.</mat-error>
          }
        </mat-form-field>
      </div>
      <div class="in-row-grid">
        <mat-form-field class="double-column">
          <mat-label>City</mat-label>
          <input matInput formControlName="property_city" />
          @if (form?.get('property_city').hasError('required')) {
            <mat-error>Please enter a city.</mat-error>
          }
          @if (form?.get('property_city').hasError('maxlength')) {
            <mat-error>
              City can be at most {{ form.get('property_city').getError('maxlength').requiredLength }} characters.
            </mat-error>
          }
          <mat-hint>Required.</mat-hint>
        </mat-form-field>
        <mat-form-field>
          <mat-label>State</mat-label>
          <mat-select id="state" formControlName="property_state">
            @for (state of states | keyvalue: statesComparator; track state) {
              <mat-option [value]="state.key">
                {{ state.value }}
              </mat-option>
            }
          </mat-select>
          @if (form?.get('property_state').hasError('required')) {
            <mat-error>Please select a state.</mat-error>
          }
          <mat-hint>Required.</mat-hint>
        </mat-form-field>
        <mat-form-field>
          <mat-label>Zip Code</mat-label>
          <input matInput mask="00000" formControlName="property_zip_code" />
          @if (form?.get('property_zip_code').hasError('required')) {
            <mat-error>Please enter a zip code.</mat-error>
          }
          <!-- The mask formats the view only: a cloned or stored value over the limit stays in the model and blocks Next. -->
          @if (form?.get('property_zip_code').hasError('maxlength')) {
            <mat-error>
              Zip code can be at most
              {{ form.get('property_zip_code').getError('maxlength').requiredLength }} characters.
            </mat-error>
          }
          <mat-hint>Required.</mat-hint>
        </mat-form-field>
        @if (
          loggedInOrg?.type === attorneyType ||
          loggedInOrg?.id === selectedAttorneyId ||
          (options[intakeFields.PROPERTY_COUNTY] && options[intakeFields.PROPERTY_COUNTY].show)
        ) {
          <mat-form-field class="county">
            <mat-label>County</mat-label>
            <mat-select
              formControlName="county"
              [disabled]="countyClientOverridden && loggedInOrg?.type !== attorneyType"
              [required]="options[intakeFields.PROPERTY_COUNTY] && options[intakeFields.PROPERTY_COUNTY].required">
              <mat-select-trigger>
                {{ form.get('county')?.value?.name }}
              </mat-select-trigger>
              <mat-option [value]="null">-- None --</mat-option>
              @for (county of counties; track county) {
                <mat-option [value]="county">
                  {{ county.name }}
                  @if (county.county_name_override && county.county_name_override !== county.name) {
                    ({{ county.county_name_override }})
                  }
                </mat-option>
              }
            </mat-select>
            @if (form?.get('county')?.hasError('required')) {
              <mat-error>Please select a county.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.PROPERTY_COUNTY].required ? 'Required.' : 'Optional.' }}
              @if (countyClientOverridden) {
                <span
                  class="blue cursor-pointer"
                  tp="The county selection has been override with your client county rules.">
                  <i class="fa fa-info-circle blue"></i>
                  Overridden via rule.
                </span>
              }
              @if (countyMatches > 1 && !countyClientOverridden && loggedInOrg?.type === attorneyType) {
                @if (!geometryResolved) {
                  <span
                    class="red cursor-pointer"
                    tp="This zip code spans more than one county.
                      Consider setting up a client county rule to automatically override the county
                      for this zip code.">
                    <i class="fa fa-info-circle red"></i>
                    Multiple matches.
                  </span>
                } @else {
                  <span
                    class="orange cursor-pointer"
                    tp="This zip code spans more than one county. The county was matched using the property coordinates.">
                    <i class="fa fa-info-circle orange"></i>
                    Matched via coordinates.
                  </span>
                }
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (
          selectedCounty && subdivisions?.enabled && form.get('containing_subdivision') && showSubdivisionSelector()
        ) {
          <mat-form-field class="subdivision">
            <mat-label>{{ subdivisionLabel }}</mat-label>
            <mat-select
              formControlName="containing_subdivision"
              (selectionChange)="onSubdivisionChangedByUser()"
              [required]="$safeNavigationMigration(options[intakeFields.PROPERTY_SUBDIVISION]?.required)">
              @for (subdivision of subdivisionOptions; track subdivision.value) {
                <mat-option [value]="subdivision.value">
                  {{ subdivision.label }}
                </mat-option>
              }
            </mat-select>
            @if (form?.get('containing_subdivision')?.hasError('required')) {
              <mat-error>Please select a {{ subdivisionLabelLower }}.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.PROPERTY_SUBDIVISION]?.required ? 'Required.' : 'Optional.' }}
              @switch (subdivisionHint()) {
                @case ('manual') {
                  <span
                    class="blue cursor-pointer"
                    [tp]="
                      'This ' + subdivisionLabelLower + ' was selected by hand, so it is never changed automatically.'
                    ">
                    <i class="fa fa-info-circle blue"></i>
                    Entered manually.
                  </span>
                }
                @case ('matched') {
                  <span
                    class="orange cursor-pointer"
                    [tp]="'The ' + subdivisionLabelLower + ' was matched using the property coordinates.'">
                    <i class="fa fa-info-circle orange"></i>
                    Matched via coordinates.
                  </span>
                }
                @case ('none') {
                  <span class="red">
                    <i class="fa fa-info-circle red"></i>
                    Could not determine the {{ subdivisionLabelLower }} from the property address. Select it manually.
                  </span>
                }
                @case ('ambiguous') {
                  <span class="red">
                    <i class="fa fa-info-circle red"></i>
                    The property address falls on a {{ subdivisionLabelLower }} boundary. Select the
                    {{ subdivisionLabelLower }} manually.
                  </span>
                }
                @case ('no-coordinates') {
                  <span>
                    Enter the address with autocomplete to detect the {{ subdivisionLabelLower }}, or select it
                    manually.
                  </span>
                }
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (selectedCounty && subdivisionKind === 'PRECINCT' && form.get('place') && showPlaceInput()) {
          <mat-form-field class="place">
            <mat-label>Place</mat-label>
            <input
              matInput
              formControlName="place"
              [required]="$safeNavigationMigration(options[intakeFields.PROPERTY_PLACE]?.required)" />
            @if (form?.get('place')?.hasError('required')) {
              <mat-error>Please enter a place.</mat-error>
            }
            <mat-hint>{{ options[intakeFields.PROPERTY_PLACE]?.required ? 'Required.' : 'Optional.' }}</mat-hint>
          </mat-form-field>
        }
        @if (courts?.length && courtSelectionEnabled && (options[intakeFields.PROPERTY_COURT]?.show ?? true)) {
          <ee-courthouse-selector
            class="courthouse-selector"
            [courts]="courts"
            [formControl]="$any(form.get('courts'))"
            label="Court(s)"
            hint="Optional.">
            @if (courtAutoSelectionSource === 'subdivision') {
              <span
                class="blue cursor-pointer"
                tp="The court was automatically selected because the property address falls within a township/subdivision served by this court.">
                <i class="fa fa-info-circle blue"></i>
                Matched via township.
              </span>
            } @else if (courtAutoSelectionSource === 'default') {
              <span
                class="orange cursor-pointer"
                tp="No court matched the property's township. The default court for this county was selected.">
                <i class="fa fa-info-circle orange"></i>
                Default court (no township match).
              </span>
            }
          </ee-courthouse-selector>
        }
      </div>

      @if (canRequestCourtChange && courtSelectionEnabled && (options[intakeFields.PROPERTY_COURT]?.show ?? true)) {
        <div class="court-change-request-row">
          <button
            mat-button
            type="button"
            class="request-court-change-button"
            [disabled]="courtsLoading || courtsLoadFailed"
            tp="Request that our team add a missing court or correct an existing one. Your request is submitted for review — it does not change the court immediately."
            (click)="openCourtChangeRequest()">
            Can't find your court? Request a change
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .margin-bottom {
        margin-bottom: 1rem;
      }
      .margin-bottom-sm {
        margin-bottom: 0.5rem;
      }
      .property-type-label {
        font-size: 1rem;
      }

      // The county, subdivision and place hints share one palette: all three explain how a
      // jurisdiction field got its value, and reading them as one thing is the point.
      .county,
      .subdivision,
      .place {
        .red {
          color: var(--primary-red);
        }
        .blue {
          color: var(--c-blue);
        }
        .orange {
          color: var(--primary-orange, #f59e0b);
        }
      }

      .courthouse-selector {
        display: block;

        .blue {
          color: var(--c-blue);
        }
        .orange {
          color: var(--primary-orange, #f59e0b);
        }
      }

      .court-change-request-row {
        margin-top: 4px;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EvictionPropertySectionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() demoMode = false;
  readonly unitNumberMax = PROPERTY_FIELD_MAX_LENGTH.property_unit_number;

  @Input() form: UntypedFormGroup;

  @Input() loggedInOrg?: Organization;

  /**
   * Whether this render may choose a court.
   *
   * The intake host sets this from "an attorney is filling the form" (`!forceIntakeForm` and an
   * attorney org). Public intake and every other client-facing render pass false, including when
   * the public link knows the firm and would otherwise look like an attorney org. Unset keeps the
   * edit dialog and any host that does not know about the flag on the signed-in org type.
   *
   * Client-facing renders leave court empty and do not call `GET /api/counties/{id}` (the court
   * list). County lists stay on `GET /api/counties/state/{state}` and the zip and geometry lookups.
   */
  @Input() allowCourtSelection: boolean | null = null;

  /**
   * The signed-in user, used only to gate the "request a court change" control (EE-386 Req 7.5/8.2).
   * Absent in demo/preview contexts (intake-form builder), where the control must stay hidden.
   */
  @Input() loggedInUser?: User;

  @Input() selectedAttorneyId: string;

  @Input() options: Map<string, IntakeOption> | undefined;

  @Input() overrideFieldVisibility = false;

  @Input() propertySection: IntakeFormSection | undefined;

  @Input() selectedClient: Organization | undefined = undefined;

  @Input() propertySectionLocalization: { [key: string]: string | undefined } = {};

  /** True when the selected county was resolved via lat/lng geometry match */
  geometryResolved = false;

  intakeFields = IntakeFormFields;
  states = STATES;
  readonly statesComparator = STATES_COMPARATOR;
  counties: County[];
  attorneyType = AccountType.ATTORNEY;

  /**
   * Court is chosen by an attorney filling the form, or later in the inbox. Client-facing
   * renders hide the picker and skip the protected court lookup.
   */
  get courtSelectionEnabled(): boolean {
    if (this.allowCourtSelection !== null) {
      return this.allowCourtSelection;
    }
    return this.loggedInOrg?.type === this.attorneyType;
  }

  countyMatches = 0;
  countyClientOverridden = false;
  courts: Court[] | undefined = undefined;
  initialCounty: County | undefined = undefined;

  /** Tracks how the court was auto-selected: 'subdivision' match, 'default' fallback, or null (no auto-selection) */
  courtAutoSelectionSource: 'subdivision' | 'default' | null = null;

  /**
   * The selected county's venue-subdivision configuration (EE-364 R2.1.5) — whether this county
   * has a sub-county venue layer at all, what it is called, and the names to offer. Null until the
   * first county is resolved; `enabled = false` for every county outside a supported state.
   */
  subdivisions: CountySubdivisions | null = null;

  /**
   * Outcome of the last boundary lookup (EE-364). Only drives the hint when the form carries no
   * `containing_subdivision_source` yet — a stored/derived source is the better answer and wins in
   * {@link subdivisionHint}.
   */
  // 'matched' is deliberately absent: a match is represented by
  // containing_subdivision_source = GEOLOCATION, and subdivisionHint() derives 'matched' from that
  // source, never from this field.
  subdivisionLookupState: 'none' | 'ambiguous' | 'no-coordinates' | null = null;

  private subs: Subscription[] = [];
  private recommendCourtSub: Subscription | null = null;
  private courtsFetchSub: Subscription | null = null;
  private subdivisionLookupSub: Subscription | null = null;
  private subdivisionsSub: Subscription | null = null;

  /**
   * The county whose subdivision-configuration request has SETTLED — successfully, in which case
   * {@link subdivisions} holds it, or with an error, in which case `subdivisions` is null. Two jobs
   * ride on that one fact:
   *
   * - "is this a different county?", which cannot be answered from the county control alone:
   *   `processCounties` nulls it while it re-resolves the list, and a transient null would look
   *   like a county change and refetch the options on every address edit;
   * - "may the court be recommended yet?" — `tryRecommendCourt` needs this county's kind to decide
   *   whether `place` may be sent, so it refuses until this matches.
   *
   * A failed request still settles, so the recommendation proceeds with today's behaviour; the
   * county is not stranded, because the refetch test also requires `subdivisions` to be non-null.
   */
  private subdivisionsCountyId: string | undefined = undefined;
  /**
   * The county the current `containing_subdivision` value belongs to: the one whose lookup matched
   * it, the one selected when the person chose it, or the case's own county for a stored value.
   * Undefined when the value's origin is unknown (a host pre-filled the form), which is treated
   * conservatively — see {@link dropValueTheCountyCannotShow}.
   */
  private subdivisionValueCountyId: string | undefined = undefined;

  /**
   * The county whose court list {@link courts} holds. `courts` alone cannot answer that: on the
   * `court_ids` architecture the list arrives asynchronously, so between selecting a new county and
   * its courts landing, `courts` still holds the PREVIOUS county's — a non-empty list for the wrong
   * county. `tryRecommendCourt` would then recommend against it, and recommend again when the real
   * list arrived: two requests for one pass.
   */
  private courtsCountyId: string | undefined = undefined;

  constructor(
    private countyService: CountyService,
    private dialog: MatDialog
  ) {}

  /**
   * The in-app court-change-request control is limited to an attorney-org admin — the ATTORNEY role
   * AND either Admin or Account Admin (EE-386 Req 7.5/8.2). It reads the user's `authorities`, which the
   * current-user endpoint builds with the same `currentAuthorities()` the backend's
   * `hasRole(ATTORNEY) and (hasAuthority(ROLE_ADMIN) or hasAuthority(ACCOUNT_ADMIN))` gate checks, so
   * the control shows exactly for users who would be authorized. The denormalized flags diverge:
   * `is_attorney` is the per-user licensed-attorney flag (not the org role, and not set on the current
   * user), and `is_admin` is true for any `admin:*` permission, including custom permission groups.
   */
  get canRequestCourtChange(): boolean {
    const authorities: unknown[] = this.loggedInUser?.authorities ?? [];
    return (
      authorities.includes(RolesEnum.ATTORNEY) &&
      (authorities.includes(RolesEnum.ADMIN) || authorities.includes(RolesEnum.ACCOUNT_ADMIN))
    );
  }

  /**
   * True while the selected county's court list is still being fetched. The request dialog is seeded
   * with {@link courts} and defaults to NEW_COURT on an empty list, so opening it mid-fetch would offer
   * an existing court as a duplicate new one. The fetch subscription closes on success or error (and on
   * a county switch, which starts a new one), so a settled empty list still leaves the control enabled.
   */
  get courtsLoading(): boolean {
    return !!this.courtsFetchSub && !this.courtsFetchSub.closed;
  }

  /**
   * True when the county's court fetch failed and there were no embedded courts to fall back on. The
   * court list is then unknown, not empty — opening the request dialog on it would default an existing
   * court to NEW_COURT exactly as a mid-fetch click would. Reset by the next fetch or county change.
   */
  courtsLoadFailed = false;

  /**
   * Open the court-change-request modal, seeding it with the courts currently offered plus the case's
   * state and county so the requester does not retype what the picker already knows. Submitting only
   * opens a request for admin review; the dialog itself confirms that and never changes a court.
   */
  openCourtChangeRequest(): void {
    const data: CourtChangeRequestDialogData = {
      courts: this.courts ?? [],
      selectedCourtId: (this.form.get('courts')?.value as Court[] | null)?.[0]?.id ?? undefined,
      state: this.form.get('property_state')?.value ?? undefined,
      countyOrVenue: (this.form.get('county')?.value as County | undefined)?.name ?? undefined
    };
    this.dialog.open(CourtChangeRequestDialogComponent, {
      data,
      width: '640px',
      autoFocus: false
    });
  }

  ngOnInit() {
    // An explicit client-facing host (public intake, client portal, send-to-attorney) submits
    // with no court. A prefilled or cloned value must not ride along. The edit dialog leaves
    // the flag unset, so an existing court on a case being edited is left alone.
    if (this.allowCourtSelection === false) {
      this.form.get('courts')?.setValue([], { emitEvent: false });
    }

    // Subscribe to state changes to trigger county lookup
    this.subs.push(
      this.form.get('property_state').valueChanges.subscribe(() => {
        this.stateChanged();
      })
    );

    // Subscribe to zip code changes to re-sort counties by zip match (debounced, 5 digits only).
    // Address autocomplete sets zip with emitEvent:false to avoid duplicate county fetches.
    this.subs.push(
      this.form
        .get('property_zip_code')
        .valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          filter((zip: string) => zip?.length === 5)
        )
        .subscribe(() => {
          this.stateChanged();
        })
    );

    this.initialCounty = this.form.get('county')?.value ?? undefined;
    if (this.form.get('containing_subdivision')?.value) {
      this.subdivisionValueCountyId = this.initialCounty?.id;
    }

    // Courts for the county the form opened with (an edit). Started here, not in ngAfterViewInit:
    // the fetch makes `courtsLoading` true, and flipping a bound value after the first check threw
    // NG0100 on the court-change button in the edit dialog.
    if (this.initialCounty && this.courtSelectionEnabled) {
      this.initializeCourtsFromCounty(this.initialCounty);
    }

    // Place narrows a multi-candidate court match (EE-364 R2.4.2/R2.4.4), so a typed place has to
    // re-ask for the recommendation. Debounced like the zip handler above so a person typing "12"
    // asks once, not twice.
    if (this.form.get('place')) {
      this.subs.push(
        this.form
          .get('place')
          .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
          .subscribe(() => this.tryRecommendCourt(this.selectedCounty))
      );
    }

    const clientSettings = this.selectedClient?.client_settings;
    if (
      clientSettings?.address_fill_option === CaseAddressFillOptions.FILL_WITH_CLIENT_ADDRESS + '' &&
      !this.form.get('property_street_address').value
    ) {
      this.form.patchValue({
        property_street_address: this.selectedClient.street_address_1,
        property_city: this.selectedClient.city,
        property_state: this.selectedClient.state,
        property_zip_code: this.selectedClient.zip_code
      });
      // valueChanges on property_state will trigger stateChanged()
    } else if (this.loggedInOrg && this.form.get('property_state').value) {
      // State already set (e.g., editing existing eviction) — trigger initial county load
      this.stateChanged(false);
    }

    if (this.selectedClient?.property_name) {
      this.form.patchValue({ property_name: this.selectedClient.property_name });
    }

    if (
      clientSettings?.address_fill_option === CaseAddressFillOptions.AUTO_COMPLETE_ADDRESS + '' &&
      clientSettings.preselect_address_state
    ) {
      this.form.patchValue({ property_state: clientSettings.preselect_address_state });
      // valueChanges on property_state will trigger stateChanged()
    }
  }

  ngAfterViewInit() {
    if (this.form.get('county')) {
      // Subscribe to county changes
      this.subs.push(
        this.form.get('county').valueChanges.subscribe((selectedCounty: County | undefined) => {
          // Whatever recommendation the PREVIOUS county had in flight is now answering a question
          // nobody is asking. Drop it first, before anything below can install the new county's
          // court list: a late response looks up its court in `this.courts`, so once that list is
          // the new county's, a shared court id or name would let a stale answer set this county's
          // court and its badge.
          //
          // `courtAutoSelectionSource` is deliberately NOT cleared here. Every path that may
          // legitimately re-make the selection clears it inside `tryRecommendCourt`, past that
          // method's guards; clearing it here would instead wipe the badge on the one case those
          // guards exist to protect — a re-selection of the county an edit form was opened on,
          // where the selection is not re-made and its explanation must survive. The only path
          // that skips the reset is "this county has no courts", and the badge is rendered only
          // beside a court list, so it cannot be seen until courts arrive — at which point
          // `tryRecommendCourt` runs and resets it.
          this.recommendCourtSub?.unsubscribe();
          this.recommendCourtSub = null;

          // The same goes for the previous county's court-LIST fetch, and for the same reason:
          // its response overwrites `this.courts` and `courtsCountyId` and preselects a court, so
          // a late one would install the wrong county's options and mis-attribute them — which
          // `tryRecommendCourt` now trusts. Cancelling here covers the branches below that install
          // a list without asking for one (an embedded-courts county, and the transient null
          // `processCounties` patches); `fetchCourtsForCounty` cancels again on its own way in.
          this.courtsFetchSub?.unsubscribe();
          this.courtsFetchSub = null;

          // Client-facing renders may select a county and must not select a court. The court
          // list is `GET /api/counties/{id}`, which is authenticated, so a public visitor
          // would 401. County lists are a different call and still run from `stateChanged`.
          if (!this.courtSelectionEnabled) {
            this.courtsLoadFailed = false;
            this.courts = undefined;
            this.courtsCountyId = undefined;
          } else if (selectedCounty?.court_ids?.length > 0) {
            // Check if county has court_ids (new architecture) - fetch courts from API
            this.fetchCourtsForCounty(selectedCounty);
          } else {
            // Use embedded courts (legacy) or clear courts
            this.courtsLoadFailed = false;
            this.courts = selectedCounty?.courts ?? undefined;
            this.courtsCountyId = selectedCounty?.id;
            this.preselectCourt(selectedCounty);
          }

          // No court recommendation from here: whether `place` may be sent depends on this
          // county's subdivision kind, which is not known until its configuration arrives. The
          // config → resolution flow below owns the recommendation, and `tryRecommendCourt`
          // refuses to run before the config has settled for the county it is handed.
          this.onCountySelectedForSubdivision(selectedCounty);
        })
      );
    }
  }

  /**
   * Initialize courts from a county (on component init).
   * This preserves the existing court selection from the form.
   */
  private initializeCourtsFromCounty(county: County): void {
    // Check if county has court_ids (new architecture) - fetch courts from API
    if (county?.court_ids?.length > 0) {
      // Deliberately the SAME subscription field the county handler cancels: an initial-load fetch
      // that lands after the user has already changed county would otherwise install this county's
      // list over the new one's.
      this.courtsFetchSub?.unsubscribe();
      this.courtsLoadFailed = false;
      this.courtsFetchSub = this.countyService.getCountyWithCourts(county.id).subscribe({
        next: (countyWithCourts: County) => {
          this.courts = countyWithCourts.courts ?? undefined;
          this.courtsCountyId = county.id;
          // Don't preselect - preserve existing selection from form
        },
        error: () => {
          // Fallback to embedded courts if API call fails
          this.courts = county?.courts ?? undefined;
          this.courtsCountyId = county.id;
          this.courtsLoadFailed = !this.courts?.length;
        }
      });
    } else {
      // Use embedded courts (legacy)
      this.courtsLoadFailed = false;
      this.courts = county?.courts ?? undefined;
      this.courtsCountyId = county?.id;
    }
  }

  /**
   * Fetch courts from the API for a county that has court_ids.
   */
  private fetchCourtsForCounty(selectedCounty: County): void {
    // Whatever list was in flight is for a county that is no longer selected: its response would
    // overwrite `this.courts` / `courtsCountyId` and preselect a court for that old county.
    this.courtsFetchSub?.unsubscribe();
    this.courtsFetchSub = null;
    this.courtsLoadFailed = false;

    // Drop the previous county's list before asking for this one, exactly as `fetchSubdivisions`
    // drops the previous configuration: holding it would offer the wrong county's courts in the
    // selector and let `tryRecommendCourt` recommend against them. The form's `courts` VALUE is
    // deliberately left alone — the stored selection is not the option list, and `preselectCourt`
    // below is the only thing entitled to change it.
    if (this.courtsCountyId !== selectedCounty.id) {
      this.courts = undefined;
      this.courtsCountyId = undefined;
    }

    this.courtsFetchSub = this.countyService.getCountyWithCourts(selectedCounty.id).subscribe({
      next: (countyWithCourts: County) => {
        this.courts = countyWithCourts.courts ?? undefined;
        this.courtsCountyId = selectedCounty.id;
        this.preselectCourt(selectedCounty);
        // Courts and the subdivision configuration are two independent requests, so either can
        // land last. Both ask; `tryRecommendCourt`'s own guards make the earlier arrival a no-op,
        // so whichever completes the pair is the one that recommends.
        this.tryRecommendCourt(selectedCounty);
      },
      error: () => {
        // Fallback to embedded courts if API call fails
        this.courts = selectedCounty?.courts ?? undefined;
        this.courtsCountyId = selectedCounty.id;
        this.courtsLoadFailed = !this.courts?.length;
        this.preselectCourt(selectedCounty);
        // The fallback list settles the pair just as the fetched one does, so this path owes the
        // recommendation exactly as the success path does. Without it, a courts-fetch failure
        // after the configuration had already settled skipped the recommendation altogether —
        // nothing else was left to ask.
        this.tryRecommendCourt(selectedCounty);
      }
    });
  }

  /**
   * Pre-select a court based on the selected city or default to the first court.
   */
  private preselectCourt(selectedCounty: County | undefined): void {
    // pre-select the court if it's not already selected
    if (!this.initialCounty || this.initialCounty.id !== selectedCounty?.id) {
      if (selectedCounty) {
        // select court based on selected city - or first one
        if (this.courts?.length > 0) {
          const city = this.form.get('property_city').value;
          const matchingCourt = this.courts.find((c) => c.cities?.includes(city));
          if (matchingCourt) {
            this.form.get('courts').setValue([matchingCourt]);
          } else {
            this.form.get('courts').setValue([this.courts[0]]);
          }
        } else {
          this.form.get('courts').setValue([]);
        }
      } else {
        this.courts = undefined;
        this.form.get('courts').setValue([]);
      }
    }
  }

  /**
   * After courts are loaded, call the recommend-court endpoint if containing_subdivision is available.
   * Auto-selects the returned court and tracks the selection source for UI indicators.
   *
   * `place` rides along whenever the case has one AND the county's subdivisions are precincts
   * (EE-364 R2.4.4): for a Texas JP precinct the subdivision alone leaves two candidate courts and
   * the place picks between them, but a place left on a case that has since moved to a township
   * county would narrow that county's match on a field it does not use.
   */
  private tryRecommendCourt(selectedCounty: County | undefined): void {
    // The court list is read when the response arrives, not now: a list that lands in between still
    // belongs to the request that asked again from its own handler. The guards below are the shared
    // ones — a client-facing render, a list or a subdivision config that still belongs to another
    // county, and the county the form opened with. None of those may clear the badge.
    const decision = courtRecommendation({
      courtSelectionEnabled: this.courtSelectionEnabled,
      hasCounty: !!selectedCounty,
      countyId: selectedCounty?.id,
      courtCount: this.courts?.length ?? 0,
      courtsCountyId: this.courtsCountyId,
      subdivisionsCountyId: this.subdivisionsCountyId,
      hasInitialCounty: !!this.initialCounty,
      initialCountyId: this.initialCounty?.id,
      courtLockedByUser: false,
      subdivision: this.form.get('containing_subdivision')?.value,
      subdivisionKind: this.subdivisionKind,
      place: this.form.get('place')?.value
    });
    if (decision.status === 'skip') return;
    this.courtAutoSelectionSource = null;
    if (decision.status !== 'recommend') return;

    // Cancel any in-flight court recommendation to prevent stale responses
    this.recommendCourtSub?.unsubscribe();
    this.recommendCourtSub = this.countyService
      .recommendCourt(decision.countyId, decision.subdivision, decision.place)
      .subscribe({
        next: (recommendedCourt: Court | null) => {
          const match = matchRecommendedCourt(this.courts ?? [], recommendedCourt, decision.subdivision);
          if (!match) return;
          this.form.get('courts').setValue([match.court]);
          this.courtAutoSelectionSource = match.source;
        },
        error: () => {
          // Silently fail — existing court selection remains
        }
      });
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
    this.countyLookupSub?.unsubscribe();
    this.recommendCourtSub?.unsubscribe();
    this.courtsFetchSub?.unsubscribe();
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionsSub?.unsubscribe();
  }

  /** The county currently selected in the form — the one whose subdivisions the selector lists. */
  get selectedCounty(): County | undefined {
    return this.form?.get('county')?.value ?? undefined;
  }

  /**
   * Visibility of the **Subdivision_Selector**: the same attorney-only rule as the court picker.
   * Precinct / township is venue, and venue is the attorney's call — a client or public filer never
   * sees it, whatever the form's `property_subdivision.show` says. Their submission still gets one:
   * the server resolves it from the property coordinates on submit (`SubdivisionVenueService`).
   *
   * The old rule compared `loggedInOrg?.id` with `selectedAttorneyId`, which never reaches this
   * component, so the public link (no org) always showed it — the Texas demo finding.
   *
   * The caller still gates on the county actually having a subdivision layer; this answers only
   * "may this user see it".
   */
  showSubdivisionSelector(): boolean {
    return this.courtSelectionEnabled;
  }

  /**
   * Visibility of the Place input: attorney-only, like the subdivision selector. Place is attorney
   * staff's tactical choice of judge.
   *
   * The caller still gates on the county's subdivision kind being `PRECINCT`; this answers only
   * "may this user see it".
   */
  showPlaceInput(): boolean {
    return this.courtSelectionEnabled;
  }

  /**
   * What this county calls its subdivisions — "Precinct" in Texas, "Town / Village" where a
   * municipality boundary is in play, "Township" otherwise. The server derives it from the kind
   * (R2.1.5) so one field is labelled one way for the whole county.
   */
  get subdivisionLabel(): string {
    return this.subdivisions?.label ?? 'Subdivision';
  }

  /** The label as it reads inside a sentence ("Could not determine the precinct ..."). */
  get subdivisionLabelLower(): string {
    return this.subdivisionLabel.toLowerCase();
  }

  /** The selected county's subdivision kind, or null when the county has no subdivision layer. */
  get subdivisionKind(): SubdivisionKind | null {
    return this.subdivisions?.kind ?? null;
  }

  /**
   * The selector's options: the county's subdivision names in natural order, plus — when the
   * stored value is not one of them (a bulk-imported value that did not match) — the stored value
   * itself, labelled so it reads as the exception it is (Req 3.9a / R2.5.1). Without this the
   * control would hold a value with no matching option, showing blank and hiding a real data
   * problem.
   */
  get subdivisionOptions(): { value: string; label: string }[] {
    const options = (this.subdivisions?.options ?? []).map((name) => ({ value: name, label: name }));

    const stored = this.form?.get('containing_subdivision')?.value;
    if (stored && !options.some((option) => option.value === stored)) {
      options.push({ value: stored, label: `${stored} (not a listed ${this.subdivisionLabelLower})` });
    }

    return options;
  }

  /**
   * Which hint to show under the selector. Provenance is the better answer whenever the case has
   * it — a stored MANUAL/GEOLOCATION source is meaningful on a form opened without any lookup —
   * so the lookup outcome only speaks when there is no value to explain (Req 3.3–3.7).
   *
   * A non-blank value with NO source reads as GEOLOCATION (EE-364 R2.2.2, ruling R25): every
   * `containing_subdivision` written before provenance was tracked was written by automation, so
   * "matched" is the true answer for it, not a shrug. A person's choice is always stamped MANUAL
   * explicitly, so nothing a person did can be mislabelled this way. It changes the label only —
   * such a value is still re-resolvable and still cleared by a no-match, exactly as before.
   */
  subdivisionHint(): 'manual' | 'matched' | 'none' | 'ambiguous' | 'no-coordinates' | null {
    const source = this.form?.get('containing_subdivision_source')?.value;
    if (source === SubdivisionSource.MANUAL) {
      return 'manual';
    }
    if (source === SubdivisionSource.GEOLOCATION) {
      return 'matched';
    }
    if (!source && this.form?.get('containing_subdivision')?.value) {
      return 'matched';
    }
    return this.subdivisionLookupState;
  }

  /**
   * The user picked a subdivision from the dropdown: record it as their choice, so no later lookup
   * overwrites it (Req 3.7, 3.9). `mat-select`'s `(selectionChange)` fires only for user
   * interaction, so a programmatic `setValue` from the lookup never lands here. A deliberate
   * choice is also a new answer for the court recommendation (R2.5.6).
   */
  onSubdivisionChangedByUser(): void {
    this.form.get('containing_subdivision_source')?.setValue(SubdivisionSource.MANUAL);
    this.form.get('containing_subdivision_kind')?.setValue(this.subdivisionKind);
    this.subdivisionValueCountyId = this.selectedCounty?.id;
    this.subdivisionLookupState = null;

    // An in-flight lookup would arrive after the choice was made.
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;

    this.tryRecommendCourt(this.selectedCounty);
  }

  /**
   * Called for every county selection. A different county needs its own option list, so the
   * configuration is refetched — but the stored value is NOT cleared here: the property did not
   * move, so a GEOLOCATION value is re-resolved from the same coordinates and a MANUAL one stands
   * (EE-364 R2.5.2, matching the server's rule in R2.3.2). The one exception is a county that
   * cannot show the value at all — see {@link dropValueTheCountyCannotShow}.
   */
  private onCountySelectedForSubdivision(selectedCounty: County | undefined): void {
    if (!selectedCounty) {
      // Either the user chose "-- None --", or `processCounties` nulled the control while it
      // re-resolves the list. Cancel in-flight work so a late response cannot land against a
      // county that is no longer selected, and keep the cached configuration: the same county
      // usually comes straight back, and refetching it on every address edit buys nothing.
      this.subdivisionsSub?.unsubscribe();
      this.subdivisionsSub = null;
      this.subdivisionLookupSub?.unsubscribe();
      this.subdivisionLookupSub = null;
      return;
    }

    // `!this.subdivisions` covers the county whose last request FAILED: it settled, so the id
    // matches, but there is no configuration to resolve against — ask again rather than strand it.
    if (this.subdivisionsCountyId !== selectedCounty.id || !this.subdivisions) {
      this.fetchSubdivisions(selectedCounty);
      return;
    }

    this.resolveSubdivision(selectedCounty);
  }

  /**
   * One request per county for the selector's whole configuration (R2.1.5). A county outside a
   * supported state answers `enabled = false`, and nothing else happens: no boundary lookup, no
   * rendered control (Req 8.3 / R2.6.1).
   */
  private fetchSubdivisions(selectedCounty: County): void {
    this.subdivisionsSub?.unsubscribe();
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;

    // Drop the previous county's configuration BEFORE asking for this one. Holding it through the
    // request would render the old county's label, options and — for a Texas county followed by a
    // North Carolina one — the Place input, all against the new county, and would let
    // `tryRecommendCourt` decide `place` from the wrong county's kind. Clearing the id here is
    // what closes that second window: nothing recommends until this request settles.
    this.subdivisions = null;
    this.subdivisionsCountyId = undefined;
    this.subdivisionLookupState = null;

    this.subdivisionsSub = this.countyService.getSubdivisions(selectedCounty.id).subscribe({
      next: (result: CountySubdivisions) => {
        this.subdivisions = result;
        this.subdivisionsCountyId = selectedCounty.id;
        this.dropValueTheCountyCannotShow(selectedCounty);
        this.resolveSubdivision(selectedCounty);
      },
      error: () => {
        // No configuration means no selector and no boundary lookup. The request still SETTLED,
        // though, so record that — it releases `tryRecommendCourt`, which then behaves exactly as
        // it did before this feature (no subdivision kind, so no `place`). `subdivisions` stays
        // null, which is what makes the next selection of this county retry.
        this.subdivisionsCountyId = selectedCounty.id;
        this.dropValueTheCountyCannotShow(selectedCounty);
        this.tryRecommendCourt(selectedCounty);
      }
    });
  }

  /**
   * A value the newly selected county has no selector for is dropped, whatever its source.
   *
   * R2.5.2 keeps the value across a county change because the property did not move — but that
   * argument assumes the new county can SHOW it. When its configuration settles as disabled (no
   * boundaries loaded, or a state outside the supported list), the control is not rendered, so a
   * Texas precinct chosen by hand — or matched from the old coordinates — would ride invisibly into
   * a Tennessee or North Carolina case and be saved there: the server keeps a MANUAL value verbatim
   * and treats an unsupported state as a no-op (R2.3), so nothing downstream would catch it. Found
   * on staging 2026-09-09 by moving a Houston address to Clarksville and then Charlotte.
   *
   * Only a value that belongs to a DIFFERENT county is dropped ({@link subdivisionValueCountyId}):
   * on an edit form, a stored value must survive its own county's configuration being unavailable,
   * or opening the dialog would erase data. A county whose selector IS enabled keeps the value,
   * listed as "<value> (not a listed …)" (Req 3.9a), where the person can see and change it.
   */
  private dropValueTheCountyCannotShow(selectedCounty: County): void {
    if (this.subdivisions?.enabled) return;
    if (!this.form.get('containing_subdivision')?.value) return;
    // Only a value known to belong to ANOTHER county is stale. A stored value on the case's own
    // county stays, and so does one whose origin this component never saw (a host pre-filled the
    // form): that is exactly what reached the court recommendation before this feature (R2.6.1).
    if (!this.subdivisionValueCountyId || this.subdivisionValueCountyId === selectedCounty.id) return;
    this.clearSubdivision();
  }

  private clearSubdivision(): void {
    this.subdivisionValueCountyId = undefined;
    this.form.get('containing_subdivision')?.setValue(null);
    this.form.get('containing_subdivision_source')?.setValue(null);
    this.form.get('containing_subdivision_kind')?.setValue(null);
    this.subdivisionLookupState = null;
  }

  /**
   * Point-in-polygon lookup of the property coordinates against the state's venue boundaries, and
   * the hint state that goes with the answer (EE-364 Req 3.3–3.6, R2.5.2). This is the client's
   * ONE subdivision lookup — the legacy "step 4" in `processCounties` folded into here, so an
   * address change resolves the subdivision exactly once, through the county handler.
   *
   * Every early return is a case that must cost nothing: a county with no subdivision layer makes
   * no lookup at all (Req 8.3), a MANUAL choice is never re-derived (Req 3.9), and a form opened
   * on an existing case with a stored value keeps it (Req 3.8, the same `initialCounty` guard
   * `preselectCourt` uses).
   */
  private resolveSubdivision(selectedCounty: County): void {
    // Exactly one court recommendation per resolution pass, structurally rather than by six
    // remembered call sites: either a boundary lookup is now in flight — and BOTH its handlers ask
    // on every outcome — or none is, and this asks. A new early return added to
    // {@link startSubdivisionLookup} cannot forget to recommend, because returning `false` IS the
    // instruction to recommend.
    if (!this.startSubdivisionLookup(selectedCounty)) {
      this.tryRecommendCourt(selectedCounty);
    }
  }

  /**
   * Starts the boundary lookup if this pass warrants one. Returns true when a request is in
   * flight — meaning its own handlers will ask for the court recommendation — and false when the
   * pass ended here, leaving that to {@link resolveSubdivision}.
   */
  private startSubdivisionLookup(selectedCounty: County): boolean {
    // Cancel any in-flight lookup so a stale response can't overwrite a newer answer
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;

    const state = this.form.get('property_state')?.value;
    const lat = this.form.get('property_lat')?.value;
    const lng = this.form.get('property_lng')?.value;
    // No layer, no control, hidden, a person's own choice, a stored value on the county the form
    // opened with, or no coordinates. The first three that leave nothing to show also clear a stale
    // hint. A false return is the caller's signal to recommend a court anyway.
    const decision = subdivisionLookupDecision({
      enabled: !!this.subdivisions?.enabled,
      hasControl: !!this.form.get('containing_subdivision'),
      visible: this.showSubdivisionSelector(),
      manual: this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL,
      keepStored: this.initialCounty?.id === selectedCounty.id && !!this.form.get('containing_subdivision')?.value,
      hasCoordinates: lat != null && lng != null && !!state
    });
    if (decision === 'skip-clear') {
      this.subdivisionLookupState = null;
      return false;
    }
    if (decision === 'skip') return false;
    if (decision === 'no-coordinates') {
      this.subdivisionLookupState = 'no-coordinates';
      return false;
    }

    this.subdivisionLookupSub = this.countyService.lookupSubdivision(lat, lng, state).subscribe({
      next: (result: SubdivisionLookupResult) => {
        // The user chose while this was in flight. Their own selection already asked for the
        // recommendation, so this stale answer neither writes nor re-asks.
        if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL) {
          return;
        }

        const outcome = subdivisionLookupOutcome(result, this.subdivisionKind);
        if (outcome.type === 'matched') {
          // Programmatic — `(selectionChange)` does not fire, so the source stays GEOLOCATION.
          this.form.get('containing_subdivision')?.setValue(outcome.subdivision);
          this.form.get('containing_subdivision_source')?.setValue(SubdivisionSource.GEOLOCATION);
          this.form.get('containing_subdivision_kind')?.setValue(outcome.kind);
          this.subdivisionValueCountyId = selectedCounty.id;
          // A match supersedes any earlier warning; otherwise a stale 'none'/'ambiguous' could show
          // again if a host later clears the source.
          this.subdivisionLookupState = null;
        } else {
          // R2.3.1: none or ambiguous clears a derived value. A MANUAL one never gets here.
          this.clearSubdivision();
          this.subdivisionLookupState = outcome.state;
        }

        // The subdivision is the input to the court recommendation (R2.5.6), so ask now that it
        // has settled — including when it settled to nothing, which clears the auto-selection hint.
        this.tryRecommendCourt(selectedCounty);
      },
      error: () => {
        // A failed lookup is a no-match: warn and let the person select (design: error handling)
        if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL) {
          return;
        }
        this.clearSubdivision();
        this.subdivisionLookupState = 'none';
        this.tryRecommendCourt(selectedCounty);
      }
    });

    return true;
  }

  get propertyFields(): UntypedFormGroup | undefined {
    return this.form.get('custom_fields') as UntypedFormGroup;
  }

  onAddressSelected(result: AddressSuggestion) {
    const state = findKey(STATES, (s) => s === result.state_full);
    const currentState = this.form.get('property_state').value;

    // Set coordinates and zip before state so they're available when stateChanged() runs
    this.form.get('property_lat').setValue(result.lat);
    this.form.get('property_lng').setValue(result.lng);
    this.form.get('property_street_address').setValue(result.street_address);
    this.form.get('property_city').setValue(result.city);
    // Suppress zip valueChanges during autocomplete to avoid duplicate county fetches
    this.form.get('property_zip_code').setValue(result.zip_code, { emitEvent: false });

    // Precinct and place are the attorney's. A client cannot see or confirm what a cloned case carried
    // (hand-picked or not), and a newly chosen address is a different place, so it goes with the old one.
    if (!this.showSubdivisionSelector()) {
      if (this.form.get('containing_subdivision')?.value || this.form.get('containing_subdivision_source')?.value) {
        this.clearSubdivision();
      }
      if (this.form.get('place')?.value) {
        this.form.get('place')?.setValue(null, { emitEvent: false });
      }
    }

    if (state !== currentState) {
      // Setting state triggers valueChanges → stateChanged() with coordinates already available
      this.form.get('property_state').setValue(state);
    } else {
      // State didn't change — valueChanges won't fire, so trigger county lookup explicitly.
      // The subdivision is NOT resolved here: `stateChanged` is async, so the county control still
      // holds the previous county at this point and the lookup would go out with the wrong
      // countyId. `processCounties` re-patches the county (null, then the resolved one) on every
      // address edit, and the county handler re-resolves the subdivision from there — for the same
      // county as well as a new one.
      this.stateChanged();
    }
  }

  private countyLookupSub: Subscription | null = null;

  stateChanged(updateSelectedCounty = true): void {
    const state = this.form.get('property_state').value;
    const zipCode = this.form.get('property_zip_code').value;

    if (!state) {
      return;
    }

    // Cancel any in-flight county lookup to prevent race conditions
    if (this.countyLookupSub) {
      this.countyLookupSub.unsubscribe();
      this.countyLookupSub = null;
    }

    // Always fetch counties by state first, then process
    this.countyLookupSub = this.countyService.getCountiesByState(state).subscribe((counties: County[]) => {
      this.processCounties(state, counties, zipCode, updateSelectedCounty);
    });
  }

  public get unitTypeOptions(): string[] {
    let options = [...UNIT_TYPES];
    if (this.options && this.options[this.intakeFields.PROPERTY_UNIT_TYPES]?.options?.length) {
      options = this.options[this.intakeFields.PROPERTY_UNIT_TYPES]?.options;
    }

    return options;
  }

  private async processCounties(
    state: string,
    counties: County[],
    zipCode: string = null,
    updateSelectedCounty = true
  ) {
    this.counties = [];
    this.countyMatches = 0;
    this.countyClientOverridden = false;
    this.geometryResolved = false;

    if (updateSelectedCounty) {
      this.form.patchValue({ county: null });
      // Clear initialCounty so court recommendation isn't blocked when address changes
      this.initialCounty = undefined;
    }

    let zipCodeInt = 0;
    if (zipCode) {
      zipCodeInt = parseInt(zipCode, 10);
    }

    // Step 1: Populate county list using zip-code matching (original logic)
    const matchingCounties: County[] = [];

    forEach(counties, (county: County) => {
      county.matches_zip_code = zipCodeInt > 0 ? county.zip_codes.indexOf(zipCodeInt) > -1 : false;
      if (county.matches_zip_code) {
        this.counties.unshift(county);
        this.countyMatches++;
        matchingCounties.push(county);
      } else {
        this.counties.push(county);
      }
    });

    // Step 2: Check county override rules first (highest priority)
    let overrideApplied = false;
    if (updateSelectedCounty && this.selectedClient?.county_override_rules?.length && state && zipCodeInt > 0) {
      const matchingCountyOverrideRule = find(this.selectedClient.county_override_rules, {
        state,
        zip_code: zipCodeInt
      });
      if (matchingCountyOverrideRule) {
        const matchingCounty = find(this.counties, { id: matchingCountyOverrideRule.county_id });
        if (matchingCounty) {
          this.form.patchValue({ county: matchingCounty });
          this.countyClientOverridden = true;
          overrideApplied = true;
        }
      }
    }

    // Step 3: If no rule matched, try geometry match (if lat/lng available)
    if (updateSelectedCounty && !overrideApplied) {
      const lat = this.form.get('property_lat')?.value;
      const lng = this.form.get('property_lng')?.value;

      if (lat != null && lng != null) {
        try {
          const geoCounty = await firstValueFrom(this.countyService.getGeometryMatch(lat, lng, state));
          if (geoCounty) {
            const match = find(this.counties, { id: geoCounty.id });
            if (match) {
              this.form.patchValue({ county: match });
              this.geometryResolved = true;
            } else {
              geoCounty.matches_zip_code = false;
              this.counties.unshift(geoCounty);
              this.form.patchValue({ county: geoCounty });
              this.geometryResolved = true;
            }
          } else {
            this.selectFirstZipMatch(matchingCounties);
          }
        } catch {
          this.selectFirstZipMatch(matchingCounties);
        }
      } else {
        this.selectFirstZipMatch(matchingCounties);
      }
    }

    // Reselect county to make sure it's the correct object reference from the list
    if (!updateSelectedCounty) {
      const county = this.counties.find((c) => c.id === this.form.value.county?.id);
      this.form.patchValue({ county });
    }

    // The subdivision lookup used to live here as "step 4", firing in parallel with the county
    // handler's own resolution — two lookups per address change, racing to write one control.
    // It is now the county handler's job alone (`onCountySelectedForSubdivision`), which the
    // county patches above trigger, so there is exactly one lookup per address change (R2.5.6).
  }

  private selectFirstZipMatch(matchingCounties: County[]): void {
    if (matchingCounties.length > 0) {
      // Prefer a county ending in "County" over one ending in "City"
      const preferred =
        matchingCounties.length > 1
          ? matchingCounties.find((c) => c.name?.endsWith('County')) || matchingCounties[0]
          : matchingCounties[0];
      this.form.patchValue({ county: preferred });
    }
  }

  /**
   * Comparison function for mat-select to properly compare Court objects
   */
  compareCourts(court1: Court, court2: Court): boolean {
    return court1 && court2 ? court1.name === court2.name : court1 === court2;
  }

  protected readonly caseAddressFillOptions = CaseAddressFillOptions;
}
